import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DEFAULT_USER_TYPE, normalizeUserCode, type GroupDto, type RoleDto } from "@platform/core";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  Field,
  Input,
  MultiSelect,
  NativeSelect,
  useToast,
} from "@platform/ui";
import { isApiError } from "../../api/errors";
import { useApi, useConsole } from "../../context";
import { useT } from "../../i18n";
import { groupOptions } from "../../lib/access";
import { errorText } from "../../lib/errors";

/** Marker message, translated when rendered (the schema lives outside the component). */
const INVALID_CODE = "invalid-user-code";

const Schema = z.object({
  email: z.email().max(254),
  displayName: z.string().trim().min(1).max(200),
  userCode: z
    .string()
    .trim()
    .max(64)
    .refine((x) => !x || normalizeUserCode(x) !== null, { message: INVALID_CODE }),
  username: z
    .string()
    .trim()
    .max(64)
    .refine((x) => !x || normalizeUserCode(x) !== null, { message: INVALID_CODE }),
  roles: z.array(z.string()),
  userType: z.string().min(1),
  groups: z.array(z.string()),
  sendInvite: z.boolean(),
});
type FormValues = z.infer<typeof Schema>;

export function AddUserDialog({
  open,
  onOpenChange,
  roles,
  userTypes,
  groups,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  roles: RoleDto[];
  /** Selectable user type ids. */
  userTypes: readonly string[];
  groups: GroupDto[];
  onCreated: (id: string) => void;
}) {
  const { t } = useT();
  const api = useApi();
  const { can, me } = useConsole();
  const toast = useToast();
  const qc = useQueryClient();
  const form = useForm<FormValues>({
    resolver: zodResolver(Schema),
    defaultValues: {
      email: "",
      displayName: "",
      userCode: "",
      username: "",
      roles: [],
      userType: DEFAULT_USER_TYPE,
      groups: [],
      sendInvite: true,
    },
  });
  const create = useMutation({
    mutationFn: (v: FormValues) =>
      api.users.create({
        email: v.email,
        displayName: v.displayName,
        sendInvite: v.sendInvite,
        userType: v.userType,
        ...(v.userCode ? { userCode: v.userCode } : {}),
        ...(v.username ? { username: v.username } : {}),
        // Console roles are for employees only (the API answers 403 otherwise).
        ...(v.roles.length && v.userType === DEFAULT_USER_TYPE ? { roles: v.roles } : {}),
        ...(v.groups.length ? { groups: v.groups } : {}),
      }),
    onSuccess: (u) => {
      toast({ title: t("users.created"), tone: "success" });
      void qc.invalidateQueries({ queryKey: ["users"] });
      form.reset();
      onOpenChange(false);
      onCreated(u.id);
    },
    onError: (e) => {
      if (!isApiError(e)) return;
      const fe = e.fieldErrors();
      if (fe.email) form.setError("email", { message: fe.email });
      if (fe.displayName) form.setError("displayName", { message: fe.displayName });
      if (e.code === "IDENTIFIER_TAKEN") {
        if (fe.username) form.setError("username", { message: t("users.dialog.usernameTaken") });
        else form.setError("userCode", { message: t("users.dialog.userCodeTaken") });
      } else {
        if (fe.userCode) form.setError("userCode", { message: t("users.dialog.userCodeInvalid") });
        if (fe.username) form.setError("username", { message: t("users.dialog.usernameInvalid") });
      }
    },
  });
  const selected = form.watch("roles");
  const userType = form.watch("userType");
  const selectedGroups = form.watch("groups");
  const assignable = roles.filter((r) => r.id !== "owner" || me.roles.includes("owner"));
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("users.dialog.title")}
      closeLabel={t("common.close")}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="add-user-form" loading={create.isPending}>
            {t("users.dialog.submit")}
          </Button>
        </>
      }
    >
      <form id="add-user-form" noValidate onSubmit={form.handleSubmit((v) => create.mutate(v))}>
        {create.isError && (
          <Alert tone="error" className="mb-4">
            {errorText(create.error)}
          </Alert>
        )}
        <Field label={t("users.dialog.email")} required error={form.formState.errors.email?.message}>
          {(p) => <Input type="email" autoComplete="off" {...p} {...form.register("email")} />}
        </Field>
        <Field label={t("users.dialog.name")} required error={form.formState.errors.displayName?.message}>
          {(p) => <Input {...p} {...form.register("displayName")} />}
        </Field>
        <Field
          label={t("users.dialog.userCode")}
          hint={t("ident.valueNameHint")}
          error={
            form.formState.errors.userCode?.message === INVALID_CODE
              ? t("users.dialog.userCodeInvalid")
              : form.formState.errors.userCode?.message
          }
        >
          {(p) => (
            <Input
              {...p}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              data-testid="add-user-code"
              {...form.register("userCode")}
            />
          )}
        </Field>
        <Field
          label={t("users.dialog.username")}
          error={
            form.formState.errors.username?.message === INVALID_CODE
              ? t("users.dialog.usernameInvalid")
              : form.formState.errors.username?.message
          }
        >
          {(p) => (
            <Input
              {...p}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              data-testid="add-user-username"
              {...form.register("username")}
            />
          )}
        </Field>
        <Field label={t("access.userType")} hint={t("users.dialog.userTypeHint")}>
          {(p) => (
            <NativeSelect {...p} data-testid="add-user-type" {...form.register("userType")}>
              {userTypes.map((id) => (
                <option key={id} value={id}>
                  {id}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        {can("groups:write") && (
          <Field label={t("access.groups")}>
            {(p) => (
              <MultiSelect
                {...p}
                label={t("access.groups")}
                value={selectedGroups}
                onChange={(v) => form.setValue("groups", v)}
                options={groupOptions(groups)}
                placeholder={t("common.none")}
                emptyText={t("groups.empty")}
                searchPlaceholder={t("common.search")}
              />
            )}
          </Field>
        )}
        {can("roles:write") && userType !== DEFAULT_USER_TYPE && (
          <p className="mb-4 text-xs text-ink-2">{t("access.rolesEmployeesOnly")}</p>
        )}
        {can("roles:write") && userType === DEFAULT_USER_TYPE && (
          <fieldset className="mb-4">
            <legend className="mb-2 text-sm font-medium">{t("users.dialog.roles")}</legend>
            <div className="grid grid-cols-2 gap-2">
              {assignable.map((r) => (
                <Checkbox
                  key={r.id}
                  label={r.name}
                  checked={selected.includes(r.id)}
                  onChange={(e) =>
                    form.setValue(
                      "roles",
                      e.target.checked ? [...selected, r.id] : selected.filter((x) => x !== r.id),
                    )
                  }
                />
              ))}
            </div>
          </fieldset>
        )}
        <Checkbox label={t("users.dialog.sendInvite")} {...form.register("sendInvite")} />
      </form>
    </Dialog>
  );
}
